import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { API_CONSTANT_MAP } from "./paths";
import { loadingBus } from "./loadingBus";
import { authBus } from "./authBus";
import { renovarToken, temTokenArmazenado } from "./renovacaoToken";
import { CHAVES, storage } from "./sessionConfig";

const getUserData = () => storage.lerJson(CHAVES.USER);

const getUnitData = () => storage.lerJson(CHAVES.UNIDADE);

const buildAuthHeaders = () => {
  const userData = getUserData();
  const unitUserData = getUnitData();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (userData?.token) {
    headers.Authorization = "Bearer " + userData.token;
  }
  const unidadeId = unitUserData
    ? unitUserData.id
    : userData?.usuario?.unidades?.[0]?.id;
  if (unidadeId !== undefined && unidadeId !== null) {
    headers["X-Unidade-Id"] = String(unidadeId);
  }
  return headers;
};

const trackStart = (config: InternalAxiosRequestConfig) => {
  loadingBus.start();
  (config as any)._loadingTracked = true;
};

const trackStop = (config: any) => {
  if (config && config._loadingTracked) {
    loadingBus.stop();
    config._loadingTracked = false;
  }
};

const api = axios.create({
  baseURL: API_CONSTANT_MAP.baseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Upload de arquivo (FormData) não pode levar `Content-Type: application/json`:
 * o multipart depende do boundary que o axios/navegador gera. Aqui o header é
 * removido para que ele seja montado sozinho.
 */
const ehFormData = (data: any) =>
  typeof FormData !== "undefined" && data instanceof FormData;

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const enviaFormData = ehFormData(config.data);
  const authHeaders = buildAuthHeaders();
  Object.entries(authHeaders).forEach(([key, value]) => {
    if (enviaFormData && key === "Content-Type") return;
    config.headers.set(key, value);
  });
  if (enviaFormData) {
    config.headers.delete("Content-Type");
  }
  trackStart(config);
  return config;
});

api.interceptors.response.use(
  (response) => {
    trackStop(response.config);
    return response;
  },
  async (error) => {
    trackStop(error?.config);

    const config: any = error?.config;
    const status = error?.response?.status;

    // Só 401 mexe na sessão. 403 é "sem permissão" no envelope da API (inclusive
    // para credencial inválida no login) e não pode deslogar o usuário.
    // O tratamento vive aqui, e não no .catch de um verbo específico, para
    // cobrir get/post/put/delete de uma vez.
    if (
      status !== 401 ||
      !config ||
      config._retentativaAuth ||
      !temTokenArmazenado()
    ) {
      return Promise.reject(error);
    }

    // Promise compartilhada: uma rajada de 401 simultâneos gera um único renew.
    const resultado = await renovarToken();

    if (resultado.status === "ok") {
      config._retentativaAuth = true; // uma única reexecução por requisição
      return api.request(config);
    }

    if (resultado.status === "invalida" || resultado.status === "indisponivel") {
      // O authBus é guardado: mesmo com N requisições falhando, a sessão é
      // encerrada (e notificada) uma única vez. Quem mostra a mensagem e
      // desloga é o AuthContext — o requestApi não fala com a UI.
      authBus.notificarSessaoExpirada("expirada");
    }

    return Promise.reject(error);
  }
);

const loginApi = axios.create({
  baseURL: API_CONSTANT_MAP.baseUrl,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
  },
});

loginApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  trackStart(config);
  return config;
});

loginApi.interceptors.response.use(
  (response) => {
    trackStop(response.config);
    return response;
  },
  (error) => {
    trackStop(error?.config);
    return Promise.reject(error);
  }
);

const requestApi = {
  get: function (
    url: string,
    byteArrayResponse: boolean = false
  ): Promise<AxiosResponse<any>> {
    const config: AxiosRequestConfig = {};
    if (byteArrayResponse) {
      config.responseType = "arraybuffer";
    }
    return api.get(url, config);
  },

  post: function (
    url: string,
    body: any,
    byteArrayResponse: boolean = false
  ): Promise<AxiosResponse<any>> {
    const config: AxiosRequestConfig = {};
    if (byteArrayResponse) {
      config.responseType = "arraybuffer";
    }
    return api.post(url, body, config);
  },

  /** POST multipart (upload de arquivo) — ver `ehFormData` acima. */
  postForm: function (url: string, form: FormData): Promise<AxiosResponse<any>> {
    return api.post(url, form);
  },

  postLogin: function (url: string, body: any) {
    return loginApi.post(url, body);
  },

  /**
   * Chamadas sem sessão (página pública de orçamento). Vão pela `loginApi`
   * de propósito: a instância `api` injeta Authorization/X-Unidade-Id e trata
   * 401 renovando a sessão — num visitante sem login isso dispararia o fluxo de
   * sessão expirada sem que exista sessão alguma.
   */
  getPublic: function (url: string): Promise<AxiosResponse<any>> {
    return loginApi.get(url);
  },

  postPublic: function (url: string, body: any): Promise<AxiosResponse<any>> {
    return loginApi.post(url, body);
  },

  put: function (url: string, body: any): Promise<AxiosResponse<any>> {
    return api.put(url, body);
  },

  delete: function (url: string): Promise<AxiosResponse<any>> {
    return api.delete(url);
  },
};

export default requestApi;
export { api, loginApi };
